import type { ReactNode } from 'react';
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateFooter,
  EmptyStateTitle,
  Link,
  Panel,
  PanelSection,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';

export type DeliveryEmptyStateProps = {
  /** Filter-aware "There are no …" sentence, shown only when filters are active. */
  emptyMessage?: string;
  hasFilters?: boolean;
  /** Drafts view uses draft-specific copy + a different docs link. */
  isDrafted?: boolean;
  /** Person-page blank slate: suppress the generic explanatory paragraph. */
  withIndividualPersonBlankSlate?: boolean;
};

type EmptyStatePanelProps = { children: ReactNode };

/** Ember renders the empty state inside the list's own panel, not bare on the page. */
function EmptyStatePanel({ children }: EmptyStatePanelProps) {
  return (
    <Panel>
      <PanelSection>{children}</PanelSection>
    </Panel>
  );
}

/**
 * The deliveries empty state. Ported from the Ember `<List>` empty-state block:
 * the filter-aware `emptyMessage` (shown only when filters are active) and the
 * fixed explanatory copy share **one** description paragraph — Pluma's
 * `EmptyState` grid maps every `EmptyStateDescription` to the same grid area, so
 * two of them would overlap. The fixed copy is suppressed for the person-page
 * blank slate; a docs link closes it out.
 */
export function DeliveryEmptyState({
  emptyMessage,
  hasFilters = false,
  isDrafted = false,
  withIndividualPersonBlankSlate = false,
}: DeliveryEmptyStateProps) {
  const filterSentence = hasFilters && !isEmptyString(emptyMessage) ? emptyMessage : '';

  if (isDrafted) {
    const description =
      `${filterSentence} View your drafted messages to see what customers will receive. Select and send the messages that are ready.`.trim();
    return (
      <EmptyStatePanel>
        <EmptyState>
          <EmptyStateTitle icon="draft">Review your messages that are ready to send</EmptyStateTitle>
          <EmptyStateDescription>{description}</EmptyStateDescription>
          <EmptyStateFooter>
            <Link href="https://docs.customer.io/queue-draft" isExternal variant="secondary" icon="help">
              Learn more about Drafts
            </Link>
          </EmptyStateFooter>
        </EmptyState>
      </EmptyStatePanel>
    );
  }

  const explainer = withIndividualPersonBlankSlate
    ? ''
    : 'View the delivery status for each message in the Sent log. Filter by status to see which messages have been queued, sent, delivered, and more.';
  const description = `${filterSentence} ${explainer}`.trim();
  return (
    <EmptyStatePanel>
      <EmptyState>
        <EmptyStateTitle icon="delivery-log">Visualize the flow of outgoing messages</EmptyStateTitle>
        {isEmptyString(description) ? null : <EmptyStateDescription>{description}</EmptyStateDescription>}
        <EmptyStateFooter>
          <Link href="https://docs.customer.io/metrics" isExternal variant="secondary" icon="help">
            Learn more about sent messages
          </Link>
        </EmptyStateFooter>
      </EmptyState>
    </EmptyStatePanel>
  );
}
